import React, { Suspense, useEffect, useState } from "react";
import "./App.css";
import { CircularProgress, createTheme, ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { Box } from "@mui/system";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";

import VendorAppointment from "./components/VendorAppointment";
import VendorFcList from "./components/VendorFCList";
import VendorRegistration from "./components/VendorRegistration";

import RaiseQuery from "./components/Raisequery";
import LoginAuth from "./RouteGuards/LoginAuth";
import AppointmentDetails from "./components/AppointmentDetails";
import { useNotificationContext } from "./common/NotificationContext";
import ProfileDetail from "./components/VendorFCList/ProfileDetail";
import PageNotFound from "./common/PageNotFound";
import AxiosInterceptorsSetup from "./utils/AxiosWithInterceptorsSetup.js";

import ChatThread from "./components/ChatThread";
import BulkAppointment from "./components/BulkAppointment";
import BulkAppointmentListDetails from "./components/BulkAppointment/BulkAppointmentListDetails";
import useGlobalState from "./Context";
import { useLoading } from "./common/LoadingContext";
import VendorAppointmentEdit from "./components/VendorAppointmentEdit";
import ResetPassword from "./components/Login/ResetPassword";
import ErrorBoundary from "./common/ErrorBoundary";
import { logout, setIsLoggedIn } from "./reducer/action";
import ForgotPassword from "./components/Login/ForgotPassword";
import OAuthRedirect from "./components/OAuthRedirect";
import Unauthorized from "./components/Unauthorized";
import UnauthorizeAuth from "./RouteGuards/UnauthorizeAuth";
function App() {
  const THEME = createTheme({
    typography: {
      fontFamily: "Poppins,sans-serif",

      button: {
        ml: 1,
        fontSize: "13px",
      },
      h6: {
        fontSize: "1rem",
      },
      body1: {
        fontSize: 14,
      },
      body2: {
        fontSize: 12,
      },
      caption: {
        fontSize: 10,
      },
    },
    palette: {
      primary: { main: "#4d6fa5" },
      secondary: {
        main: "#dfe3e9",
      },
      background: {
        default: "#f5f5f5",
        primary: "#4d6fa5",
        paper: "#ffffff",
      },
      text: {
        primary: "#000",
      },
    },
    components: {
      MuiTextField: {
        variants: [
          {
            props: { variant: "standard", disabled: true },
            style: {
              backgroundColor: "#d7d7d785",
            },
          },
          {
            props: { variant: "outlined", disabled: true },
            style: {
              backgroundColor: "#d7d7d785",
            },
          },
        ],
      },
    },
  });

  const onIdle = () => {
    logoutMethod();
  };

  const { start } = useIdleTimer({
    onIdle,
    timeout: 1000 * 60 * 15,
    promptTimeout: 0,
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
    ],
    immediateEvents: [],
    debounce: 0,
    throttle: 0,
    eventsThrottle: 200,
    element: document,
    startOnMount: true,
    startManually: true,
    stopOnIdle: true,
    crossTab: false,
    name: "idle-logout",
    syncTimers: 0,
  });
  const [{ isLoggedIn }, dispatch] = useGlobalState();
  const { setLoading } = useLoading();
  const { showNotification } = useNotificationContext();

  const logoutMethod = async () => {
    try {
      setLoading(true);
      dispatch(setIsLoggedIn(false));

      dispatch(logout());
    } catch (err) {
      showNotification("Error", err?.message ?? "Failed to Logout");
    } finally {
      setLoading(false);
    }
  };

  function AxiosInterceptorDispatch() {
    const { disableNotification } = useNotificationContext();

    const [ran, setRan] = useState(false);

    if (!ran) {
      AxiosInterceptorsSetup(dispatch, disableNotification);
      setRan(true);
    }
    return <></>;
  }
  useEffect(() => {
    if (isLoggedIn) {
      start();
    }
  }, [isLoggedIn, start]);

  useEffect(() => {
    if (isLoggedIn) {
      window.addEventListener("unload", handleTabClosing);
      return () => {
        window.removeEventListener("unload", handleTabClosing);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleTabClosing = async () => {
    if (isLoggedIn) {
      await logoutMethod();
    }
  };

  return (
    <ThemeProvider theme={THEME}>
      <CssBaseline />
      <BrowserRouter>
        {<AxiosInterceptorDispatch />}
        <ErrorBoundary>
          <Suspense fallback={<CircularProgress />}>
            <Box className="app-center">
              <Routes>
                <Route
                  path="/forgot-password-user"
                  element={<ForgotPassword />}
                ></Route>

                <Route
                  path="/vendor-registration"
                  element={<VendorRegistration />}
                ></Route>
                <Route
                  path="/oauth2/redirect"
                  element={<OAuthRedirect />}
                ></Route>

                <Route
                  path="/unauthorized"
                  element={
                    <UnauthorizeAuth>
                      <Unauthorized />
                    </UnauthorizeAuth>
                  }
                ></Route>
                <Route
                  path="/reset-password"
                  element={<ResetPassword />}
                ></Route>
                <Route path="/" element={<LoginAuth />}>
                  <Route path="/" element={<AppointmentDetails />}></Route>

                  <Route
                    path="/vendor-registration"
                    element={<VendorRegistration />}
                  ></Route>

                  <Route path="/raise-query" element={<RaiseQuery />}></Route>
                  <Route
                    path="/view-profile"
                    element={<ProfileDetail />}
                  ></Route>
                  <Route path="/add-fc" element={<VendorFcList />}></Route>
                  <Route
                    path="appointment-details"
                    element={<AppointmentDetails />}
                  ></Route>
                  <Route
                    path="/book-appointment"
                    element={<VendorAppointment />}
                  ></Route>
                  <Route
                    path="/edit-book-appointment"
                    element={<VendorAppointmentEdit />}
                  ></Route>

                  <Route
                    path="/query-chat-thread"
                    element={<ChatThread />}
                  ></Route>
                  <Route
                    path="/bulk-appointment-list"
                    element={<BulkAppointmentListDetails />}
                  ></Route>

                  <Route
                    path="/bulk-appointment"
                    element={<BulkAppointment />}
                  ></Route>

                  <Route path="*" element={<PageNotFound />} />
                </Route>
              </Routes>
            </Box>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
